// Unificado: usar la instancia central de API
import api from '@/services/api'

class PermisosService {
  // Obtener permisos disponibles
  async getPermisosDisponibles() {
    try {
      const response = await api.get('/permisos/disponibles')
      return response.data
    } catch (error) {
      console.error('Error al obtener permisos disponibles:', error)
      throw error
    }
  }

  // Obtener permisos de un usuario específico
  async getPermisosUsuario(userId) {
    try {
      const response = await api.get(`/permisos/usuario/${userId}`)
      return response.data
    } catch (error) {
      console.error('Error al obtener permisos del usuario:', error)
      throw error
    }
  }

  // Actualizar permisos de un usuario
  async updatePermisosUsuario(userId, permisos) {
    try {
      const response = await api.put(`/permisos/usuario/${userId}`, {
        permisos: permisos
      })
      return response.data
    } catch (error) {
      console.error('Error al actualizar permisos del usuario:', error)
      throw error
    }
  }

  // Verificar si un usuario tiene un permiso específico
  async usuarioTienePermiso(userId, permiso) {
    try {
      const response = await api.get(`/permisos/usuario/${userId}/check/${permiso}`)
      return response.data
    } catch (error) {
      console.error('Error al verificar permiso del usuario:', error)
      throw error
    }
  }

  // Obtener todos los permisos agrupados por módulo
  async getPermisosAgrupados() {
    try {
      const response = await api.get('/permisos/agrupados')
      return response.data
    } catch (error) {
      console.error('Error al obtener permisos agrupados:', error)
      throw error
    }
  }

  // Crear un nuevo permiso personalizado
  async crearPermiso(data) {
    try {
      const response = await api.post('/permisos', data)
      return response.data
    } catch (error) {
      console.error('Error al crear permiso:', error)
      throw error
    }
  }

  // Actualizar un permiso existente
  async actualizarPermiso(id, data) {
    try {
      const response = await api.put(`/permisos/${id}`, data)
      return response.data
    } catch (error) {
      console.error('Error al actualizar permiso:', error)
      throw error
    }
  }

  // Eliminar un permiso personalizado
  async eliminarPermiso(id) {
    try {
      const response = await api.delete(`/permisos/${id}`)
      return response.data
    } catch (error) {
      console.error('Error al eliminar permiso:', error)
      throw error
    }
  }

  // Clonar permisos de un usuario a otro
  async clonarPermisos(userIdOrigen, userIdDestino) {
    try {
      const response = await api.post('/permisos/clonar', {
        usuario_origen: userIdOrigen,
        usuario_destino: userIdDestino
      })
      return response.data
    } catch (error) {
      console.error('Error al clonar permisos:', error)
      throw error
    }
  }

  // Obtener historial de cambios de permisos
  async getHistorialPermisos(userId, limite = 50) {
    try {
      const response = await api.get(`/permisos/historial/${userId}`, {
        params: { limite }
      })
      return response.data
    } catch (error) {
      console.error('Error al obtener historial de permisos:', error)
      throw error
    }
  }

  // Exportar matriz de permisos
  async exportarMatrizPermisos() {
    try {
      const response = await api.get('/permisos/export', {
        responseType: 'blob'
      })
      return response
    } catch (error) {
      console.error('Error al exportar matriz de permisos:', error)
      throw error
    }
  }

  // Importar permisos desde archivo
  async importarPermisos(archivo) {
    try {
      const formData = new FormData()
      formData.append('archivo', archivo)
      
      const response = await api.post('/permisos/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      console.error('Error al importar permisos:', error)
      throw error
    }
  }

  // Resetear permisos de un usuario según su rol
  async resetearPermisosPorRol(userId, rol) {
    try {
      const response = await api.post(`/permisos/usuario/${userId}/reset`, {
        rol: rol
      })
      return response.data
    } catch (error) {
      console.error('Error al resetear permisos por rol:', error)
      throw error
    }
  }

  // Obtener estadísticas de permisos
  async getEstadisticasPermisos() {
    try {
      const response = await api.get('/permisos/estadisticas')
      return response.data
    } catch (error) {
      console.error('Error al obtener estadísticas de permisos:', error)
      throw error
    }
  }

  // Validar configuración de permisos
  validatePermisos(permisos) {
    const errors = []
    
    if (!Array.isArray(permisos)) {
      errors.push('Los permisos deben ser un arreglo')
      return { isValid: false, errors }
    }
    
    // Validar que cada permiso tenga el formato correcto
    permisos.forEach((permiso, index) => {
      if (typeof permiso !== 'string' || permiso.trim() === '') {
        errors.push(`El permiso en la posición ${index + 1} no es válido`)
      }
      
      // Validar formato de permiso (modulo_accion)
      if (!/^[a-z_]+_[a-z_]+$/.test(permiso)) {
        errors.push(`El permiso "${permiso}" no tiene el formato correcto (modulo_accion)`)
      }
    })
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Formatear nombre de permiso para mostrar
  formatearPermiso(permiso) {
    if (!permiso || typeof permiso !== 'string') return ''
    
    const partes = permiso.split('_')
    if (partes.length < 2) return permiso
    
    const modulo = partes[0]
    const accion = partes[1]
    
    const modulos = {
      usuarios: 'Usuarios',
      clientes: 'Clientes',
      productos: 'Productos',
      facturacion: 'Facturación',
      reportes: 'Reportes',
      empresas: 'Empresas',
      configuracion: 'Configuración'
    }
    
    const acciones = {
      view: 'Ver',
      create: 'Crear',
      edit: 'Editar',
      delete: 'Eliminar',
      export: 'Exportar',
      import: 'Importar'
    }
    
    return `${modulos[modulo] || modulo} - ${acciones[accion] || accion}`
  }

  // Agrupar permisos por módulo
  agruparPermisosPorModulo(permisos) {
    const agrupados = {}
    
    permisos.forEach(permiso => {
      const modulo = permiso.module || 'general'
      
      if (!agrupados[modulo]) {
        agrupados[modulo] = {
          nombre: this.formatearModulo(modulo),
          permisos: [],
          icono: this.getIconoModulo(modulo)
        }
      }
      
      agrupados[modulo].permisos.push(permiso)
    })
    
    return agrupados
  }

  // Formatear nombre de módulo
  formatearModulo(modulo) {
    const modulos = {
      usuarios: 'Gestión de Usuarios',
      clientes: 'Gestión de Clientes',
      productos: 'Gestión de Productos',
      facturacion: 'Facturación',
      reportes: 'Reportes y Analytics',
      empresas: 'Gestión de Empresas',
      configuracion: 'Configuración del Sistema'
    }
    
    return modulos[modulo] || modulo.charAt(0).toUpperCase() + modulo.slice(1)
  }

  // Obtener icono para módulo
  getIconoModulo(modulo) {
    const iconos = {
      usuarios: 'fas fa-users',
      clientes: 'fas fa-user-friends',
      productos: 'fas fa-box',
      facturacion: 'fas fa-file-invoice',
      reportes: 'fas fa-chart-bar',
      empresas: 'fas fa-building',
      configuracion: 'fas fa-cog'
    }
    
    return iconos[modulo] || 'fas fa-circle'
  }

  // Obtener color para módulo
  getColorModulo(modulo) {
    const colores = {
      usuarios: 'primary',
      clientes: 'success',
      productos: 'info',
      facturacion: 'warning',
      reportes: 'danger',
      empresas: 'secondary',
      configuracion: 'dark'
    }
    
    return colores[modulo] || 'light'
  }

  // Verificar dependencias entre permisos
  verificarDependencias(permisosSeleccionados, permisoNuevo) {
    // Definir dependencias entre permisos
    const dependencias = {
      'usuarios_edit': ['usuarios_view'],
      'usuarios_delete': ['usuarios_view', 'usuarios_edit'],
      'clientes_edit': ['clientes_view'],
      'clientes_delete': ['clientes_view', 'clientes_edit'],
      'productos_edit': ['productos_view'],
      'productos_delete': ['productos_view', 'productos_edit'],
      'facturacion_edit': ['facturacion_view'],
      'facturacion_delete': ['facturacion_view', 'facturacion_edit']
    }
    
    const permisosDependientes = dependencias[permisoNuevo] || []
    const permisosFaltantes = permisosDependientes.filter(
      dep => !permisosSeleccionados.includes(dep)
    )
    
    return {
      tieneDependencias: permisosDependientes.length > 0,
      dependencias: permisosDependientes,
      faltantes: permisosFaltantes,
      cumpleDependencias: permisosFaltantes.length === 0
    }
  }

  // Obtener permisos recomendados por rol
  getPermisosRecomendadosPorRol(rol) {
    const permisosPorRol = {
      admin: [
        'usuarios_view', 'usuarios_create', 'usuarios_edit', 'usuarios_delete',
        'clientes_view', 'clientes_create', 'clientes_edit', 'clientes_delete',
        'productos_view', 'productos_create', 'productos_edit', 'productos_delete',
        'facturacion_view', 'facturacion_create', 'facturacion_edit', 'facturacion_delete',
        'reportes_view', 'reportes_export',
        'empresas_view', 'empresas_edit',
        'configuracion_view', 'configuracion_edit'
      ],
      manager: [
        'usuarios_view',
        'clientes_view', 'clientes_create', 'clientes_edit',
        'productos_view', 'productos_create', 'productos_edit',
        'facturacion_view', 'facturacion_create', 'facturacion_edit',
        'reportes_view', 'reportes_export',
        'empresas_view', 'empresas_edit'
      ],
      user: [
        'clientes_view', 'clientes_create',
        'productos_view',
        'facturacion_view', 'facturacion_create'
      ],
      readonly: [
        'usuarios_view',
        'clientes_view',
        'productos_view',
        'facturacion_view',
        'reportes_view'
      ]
    }
    
    return permisosPorRol[rol] || []
  }

  // Calcular nivel de acceso
  calcularNivelAcceso(permisosUsuario, permisosDisponibles) {
    if (permisosDisponibles.length === 0) return 0
    
    const porcentaje = (permisosUsuario.length / permisosDisponibles.length) * 100
    
    if (porcentaje >= 90) return 'total'
    if (porcentaje >= 70) return 'alto'
    if (porcentaje >= 40) return 'medio'
    if (porcentaje >= 10) return 'bajo'
    return 'minimo'
  }

  // Generar reporte de permisos
  generarReportePermisos(usuario, permisos) {
    const permisosAgrupados = this.agruparPermisosPorModulo(permisos)
    const totalPermisos = permisos.length
    const permisosActivos = permisos.filter(p => p.activo).length
    
    return {
      usuario: {
        id: usuario.id,
        nombre: usuario.full_name,
        rol: usuario.role,
        email: usuario.email
      },
      estadisticas: {
        total: totalPermisos,
        activos: permisosActivos,
        inactivos: totalPermisos - permisosActivos,
        porcentaje: totalPermisos > 0 ? (permisosActivos / totalPermisos) * 100 : 0
      },
      modulos: permisosAgrupados,
      nivel: this.calcularNivelAcceso(
        permisos.filter(p => p.activo), 
        permisos
      ),
      fecha_generacion: new Date().toISOString()
    }
  }
}

export default new PermisosService()
